import { AlertTriangle } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../../../../lib/supabase';

import AccountDeletionFlow from './AccountDeletionFlow';
import { SettingsSection } from './SettingsSection';

interface DataPrivacyTabProps {
  /** When true, render only the inner sections (no scroll wrapper) for embedding in Account page */
  embedded?: boolean;
}

const DataPrivacyTab: React.FC<DataPrivacyTabProps> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [showDeletionFlow, setShowDeletionFlow] = useState(false);

  // Handle account deletion completion
  const handleDeletionComplete = () => {
    console.log('🗑️ ACCOUNT DELETION: Clearing session and redirecting');
    
    // Clear all session data FIRST
    sessionStorage.clear();
    localStorage.clear(); // Full clear since account is deleted
    
    // Sign out and redirect to home
    supabase.auth.signOut().finally(() => {
      console.log('🗑️ ACCOUNT DELETION: Complete, redirecting to home');
      window.location.href = '/';
    });
  };

  const content = (
    <>
      <div className={embedded ? 'mt-6 space-y-3 min-w-0' : 'w-full max-w-6xl py-2 px-3 sm:px-4 space-y-3 min-w-0'}>
        <SettingsSection title={t('dashboard.userProfile.dataPrivacy.deleteAccountSection')} hideRule>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-start justify-between gap-4 min-w-0">
            <div className="min-w-0 flex-1">
              <p className="vscode-text-primary text-sm font-medium mb-1">{t('dashboard.userProfile.dataPrivacy.deleteAccountTitle')}</p>
              <p className="vscode-text-secondary text-sm">
                {t('dashboard.userProfile.dataPrivacy.deleteAccountDesc')}
              </p>
              <div className="mt-3 rounded-lg bg-amber-50/40 dark:bg-amber-900/10 border border-amber-200/40 dark:border-amber-800/20 p-3">
                <div className="flex items-start gap-2 min-w-0">
                  <AlertTriangle className="w-4 h-4 text-amber-500/60 dark:text-amber-400/40 mt-0.5 shrink-0" aria-hidden />
                  <p className="vscode-text-secondary text-xs">
                    <strong className="vscode-text-primary">{t('dashboard.userProfile.dataPrivacy.warning')}:</strong> {t('dashboard.userProfile.dataPrivacy.deleteAccountWarning')}
                  </p>
                </div>
              </div>
            </div>
            <div className="shrink-0 w-full sm:w-auto flex justify-end sm:items-start">
              <button
                onClick={() => setShowDeletionFlow(true)}
                className="w-full sm:w-auto min-h-[44px] px-4 py-2.5 text-sm font-medium vscode-text-secondary rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100/90 dark:bg-gray-700/40 hover:bg-gray-200/90 dark:hover:bg-gray-600/50 transition-colors touch-manipulation"
              >
                {t('dashboard.userProfile.dataPrivacy.deleteMyAccount')}
              </button>
            </div>
          </div>
        </SettingsSection>
      </div>

      <AccountDeletionFlow
        isOpen={showDeletionFlow}
        onClose={() => setShowDeletionFlow(false)}
        onComplete={handleDeletionComplete}
      />
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden">
      {content}
    </div>
  );
};

export default React.memo(DataPrivacyTab);
